import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getToken } from "@/lib/api";
import {
  Radar, Loader2, Sparkles, Send, Ban, Plane, MapPin, Clock, CheckCircle2,
  ShieldCheck, User, DollarSign, AlertTriangle, Star, Wind, CloudSun,
} from "lucide-react";

const STATUS_META = {
  requested: { label: "Requested", cls: "bg-slate-600/30 text-slate-300" },
  offered: { label: "Offered to pilots", cls: "bg-cyan-500/15 text-cyan-300" },
  accepted: { label: "Pilot assigned", cls: "bg-indigo-500/15 text-indigo-300" },
  en_route: { label: "En route", cls: "bg-amber-500/15 text-amber-300" },
  on_scene: { label: "On scene", cls: "bg-amber-500/15 text-amber-300" },
  completed: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-red-500/15 text-red-300" },
  declined: { label: "Declined", cls: "bg-red-500/15 text-red-300" },
  no_pilots: { label: "No pilots available", cls: "bg-red-500/15 text-red-300" },
};

// Attorney-facing Crash Ready Response Network™ dispatch panel (Case Intelligence).
export default function DispatchPanel() {
  const { caseId } = useParams();
  const [d, setD] = useState(undefined);
  const [advisor, setAdvisor] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [weather, setWeather] = useState(null);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState("");
  const [rated, setRated] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/cases/${caseId}/dispatch`); setD(data.dispatch); }
    catch { setD(null); }
  };
  useEffect(() => {
    load(); api.get(`/cases/${caseId}/dispatch/advisor`).then((r) => setAdvisor(r.data)).catch(() => {});
    const t = setInterval(load, 12000); return () => clearInterval(t);
    // eslint-disable-next-line
  }, [caseId]);

  // Scene weather + flight advisory for an active dispatch.
  useEffect(() => {
    if (d && d.dispatch_id && !["completed", "cancelled", "declined", "no_pilots"].includes(d.status)) {
      api.get(`/dispatches/${d.dispatch_id}/weather`).then((r) => setWeather(r.data)).catch(() => setWeather(null));
    }
    // eslint-disable-next-line
  }, [d?.dispatch_id, d?.status]);

  const submitRating = async () => {
    try { await api.post(`/cases/${caseId}/dispatch/${d.dispatch_id}/rate`, { stars, review }); toast.success("Rating submitted"); setRated(true); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // Live GPS + status via Server-Sent Events (falls back to the 12s poll above).
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/dispatch/stream?auth=${getToken()}`;
    let es;
    try {
      es = new EventSource(url);
      es.onmessage = (e) => { try { const p = JSON.parse(e.data); if (p && p.dispatch) setD(p.dispatch); } catch { /* ignore */ } };
      es.onerror = () => { es && es.close(); };
    } catch { /* EventSource unsupported */ }
    return () => es && es.close();
  }, [caseId]);

  if (d === undefined) return <div className="mt-8 flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>;

  const active = d && !["completed", "cancelled", "declined", "no_pilots"].includes(d.status);
  const createDispatch = async () => {
    setBusy(true);
    try { await api.post(`/cases/${caseId}/dispatch`, { notes }); toast.success("Dispatch requested"); setCreating(false); setNotes(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this dispatch?")) return;
    try { await api.post(`/cases/${caseId}/dispatch/${d.dispatch_id}/cancel`, {}); toast.success("Dispatch cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const askAi = async () => { setAiBusy(true); try { const { data } = await api.post(`/cases/${caseId}/dispatch/advisor/ai`, {}); setAi(data); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setAiBusy(false); } };

  const S = d ? STATUS_META[d.status] : null;

  return (
    <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="dispatch-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Radar className="w-3.5 h-3.5" /> Crash Ready Response Network™</p>
          <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Drone Dispatch</h2>
        </div>
        {S && <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${S.cls}`} data-testid="dispatch-status"><Plane className="w-3.5 h-3.5" /> {S.label}</span>}
      </div>

      {/* AI Deployment Advisor */}
      {advisor && (
        <div className="mt-4 rounded-xl bg-slate-900/70 border border-slate-800 p-4" data-testid="dispatch-advisor">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className={`text-sm font-semibold flex items-center gap-1.5 ${advisor.recommend ? "text-emerald-300" : "text-slate-300"}`}>
              {advisor.recommend ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {advisor.label}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Value {advisor.value_score}% · {advisor.urgency} urgency</span>
              <button onClick={askAi} disabled={aiBusy} data-testid="dispatch-ask-ai" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 disabled:opacity-50">{aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Ask AI</button>
            </div>
          </div>
          <ul className="mt-2 space-y-1">{advisor.reasons.map((r, i) => <li key={i} className="text-xs text-slate-400">• {r}</li>)}</ul>
          {ai && (
            <div className="mt-3 pt-3 border-t border-slate-800 text-xs text-slate-200" data-testid="dispatch-ai-result">
              <div className="font-semibold text-cyan-300">AI advisor · {ai.confidence}% confidence · {ai.urgency || advisor.urgency}</div>
              <p className="mt-1 text-slate-300">{ai.recommended_action || ai.analysis}</p>
            </div>
          )}
        </div>
      )}

      {/* No active dispatch → request */}
      {!active && (
        <div className="mt-5">
          {d && d.status === "completed" && d.assigned_pilot_name && !rated && (
            <div className="mb-4 rounded-xl bg-slate-900/60 border border-slate-800 p-4" data-testid="dispatch-rate">
              <div className="text-sm text-white font-semibold flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-300" /> Rate {d.assigned_pilot_name}</div>
              <div className="mt-2 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => setStars(s)} data-testid={`rate-star-${s}`} className="p-0.5">
                    <Star className={`w-6 h-6 ${s <= stars ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} />
                  </button>
                ))}
              </div>
              <textarea value={review} onChange={(e) => setReview(e.target.value)} rows={2} data-testid="rate-review" placeholder="Optional review of the pilot's work…" className="mt-2 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
              <button onClick={submitRating} disabled={!stars} data-testid="rate-submit" className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-slate-950 text-sm font-semibold hover:bg-amber-300 disabled:opacity-50"><Star className="w-4 h-4" /> Submit rating</button>
            </div>
          )}
          {rated && <p className="mb-3 text-xs text-emerald-300" data-testid="dispatch-rated">Thanks — your rating was recorded.</p>}
          {d && <p className="text-xs text-slate-400 mb-2">Last dispatch: {S?.label}.</p>}
          {!creating ? (
            <button onClick={() => setCreating(true)} data-testid="dispatch-request-open" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400"><Send className="w-4 h-4" /> Request drone dispatch</button>
          ) : (
            <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="dispatch-notes" placeholder="What should the pilot capture? (e.g. skid marks, signal timing, sight lines)" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500" />
              <div className="mt-2 flex gap-2">
                <button onClick={createDispatch} disabled={busy} data-testid="dispatch-request-submit" className="flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Dispatch</button>
                <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-sm text-slate-300">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active dispatch → tracking */}
      {active && (
        <div className="mt-5 space-y-3">
          {weather && weather.available && (
            <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4" data-testid="dispatch-weather">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-white font-semibold flex items-center gap-1.5"><CloudSun className="w-4 h-4 text-cyan-400" /> Scene weather</div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${weather.flight_advisory.level === "go" ? "bg-emerald-500/15 text-emerald-300" : weather.flight_advisory.level === "caution" ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>
                  Flight: {weather.flight_advisory.level.toUpperCase()}
                </span>
              </div>
              <div className="mt-1.5 text-xs text-slate-300 flex items-center gap-3 flex-wrap">
                <span>{Math.round(weather.temperature_f)}°F</span>
                <span className="flex items-center gap-1"><Wind className="w-3 h-3" /> {Math.round(weather.wind_mph)} mph{weather.gust_mph ? ` (gust ${Math.round(weather.gust_mph)})` : ""}</span>
                {weather.precipitation_in > 0 && <span>{weather.precipitation_in}" precip</span>}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{weather.flight_advisory.reasons[0]}</div>
            </div>
          )}
          {d.pilot && (
            <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4 flex items-center justify-between flex-wrap gap-2" data-testid="dispatch-pilot">
              <div className="text-sm">
                <div className="text-white font-semibold flex items-center gap-1.5"><User className="w-4 h-4 text-cyan-400" /> {d.pilot.name} <span className="text-xs text-amber-300">★{d.pilot.rating}</span></div>
                <div className="text-xs text-slate-400 mt-0.5"><ShieldCheck className="w-3 h-3 inline" /> FAA {d.pilot.faa_cert || "—"} · {d.pilot.equipment || "—"} · {d.pilot.city}, {d.pilot.state}</div>
              </div>
              {d.fee && <div className="text-sm text-slate-300 flex items-center gap-1"><DollarSign className="w-4 h-4 text-emerald-400" />{d.fee} <span className="text-xs text-slate-500">({d.payment?.status})</span></div>}
            </div>
          )}
          {d.pilot_location && (
            <div className="rounded-xl bg-slate-900/60 border border-cyan-500/30 p-4" data-testid="dispatch-live-location">
              <div className="flex items-center justify-between">
                <div className="text-sm text-white font-semibold flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-cyan-400" /> Live pilot position
                  <span className="relative flex h-2 w-2 ml-1"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
                </div>
                <span className="text-[11px] text-slate-500">updated {new Date(d.pilot_location.at).toLocaleTimeString()}</span>
              </div>
              <div className="mt-1.5 text-xs text-slate-300 font-mono">
                {Number(d.pilot_location.lat).toFixed(5)}, {Number(d.pilot_location.lng).toFixed(5)}
                {d.pilot_location.speed != null && <span className="text-slate-500"> · {Math.round(d.pilot_location.speed)} mph</span>}
                {d.pilot_location.heading != null && <span className="text-slate-500"> · hdg {Math.round(d.pilot_location.heading)}°</span>}
              </div>
              <a href={`https://www.google.com/maps?q=${d.pilot_location.lat},${d.pilot_location.lng}`} target="_blank" rel="noreferrer"
                 className="mt-1 inline-block text-[11px] text-cyan-400 hover:underline">Open in Maps →</a>
            </div>
          )}
          {/* Live status timeline */}
          <div className="rounded-xl bg-slate-900/40 border border-slate-800 p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Live status</div>
            <div className="space-y-1.5">
              {(d.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300" data-testid={`dispatch-history-${i}`}>
                  <Clock className="w-3 h-3 text-slate-600" /><span className="capitalize text-slate-200">{(STATUS_META[h.status]?.label) || h.status}</span>
                  <span className="text-slate-600">· {new Date(h.at).toLocaleTimeString()}</span>{h.note && <span className="text-slate-500">· {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
          <button onClick={cancel} data-testid="dispatch-cancel" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-sm text-red-300 hover:bg-red-500/20"><Ban className="w-4 h-4" /> Cancel dispatch</button>
        </div>
      )}
    </div>
  );
}
